<script lang="ts" setup>
import { computed, onMounted, ref, type HTMLAttributes } from "vue";
import type { IconName } from "@/shared/@types/IconName";
import { getUniqueId } from "@/shared/helper/uniqueId";
import InputGroup from "../InputGroup/InputGroup.vue";
import TextInputCounter from "../TextInputCounter/TextInputCounter.vue";

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    label: string;
    placeholder?: string;
    size?: "s" | "m" | "l";
    errors?: Array<string>;
    name?: string;
    type?: "date" | "email" | "password" | "text" | "search" | "datetime-local";
    prependIcon?: IconName;
    disabled?: boolean;
    autocomplete?: string;
    minlength?: number;
    maxlength?: number;
    showMaxlengthCounter?: boolean;
    required?: boolean;
    min?: string;
    max?: string;
    inputmode?: HTMLAttributes["inputmode"];
    isLabelHidden?: boolean;
    infoButtonCaption?: string;
    hasInfoButton?: boolean;
    hint?: string;
    hasLinkedHint?: boolean;
    transformToUppercase?: boolean;
    forceAlphanumeric?: boolean;
    focusOnMount?: boolean;
  }>(),
  {
    size: "m",
    type: "text",
    showMaxlengthCounter: true,
    errors: () => [],
  },
);
const emits = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "openHint"): void;
  (e: "showInformation"): void;
  (e: "change", value: string): void;
  (e: "focus"): void;
}>();

// --- Text Input Counter ---
const currentInputCount = computed(() => props.modelValue?.length || 0);

// to adjust the correct padding size of the input
// eg 30 / 30 => two times the characters of maxlength plus 1 character for the slash and two for the spaces
const styleBindings = computed(() => ({
  "--input-append-size": props.maxlength
    ? `${props.maxlength.toString().length * 2 + 3}ch`
    : undefined,
}));

const inputId = getUniqueId("text-input");

const nonAlphanumericChars = /[^a-zA-Z0-9]/;

const transformValue = (event: Event) => {
  let value = (event.target as HTMLInputElement).value;

  if (props.transformToUppercase) value = value.toUpperCase();
  if (props.forceAlphanumeric) value = value.replace(nonAlphanumericChars, "");

  return value;
};

const handleInput = (event: Event) => {
  const value = transformValue(event);
  emits("update:modelValue", value);
};

const handleChange = (event: Event) => {
  const value = transformValue(event);
  emits("change", value);
};

const inputField = ref<HTMLInputElement | null>(null);

onMounted(() => {
  if (props.focusOnMount) inputField.value?.focus();
});
</script>

<template>
  <InputGroup
    :id="inputId"
    :label="label"
    :size="size"
    :info-button-caption="infoButtonCaption"
    :has-info-button="hasInfoButton"
    :hint="hint"
    :has-linked-hint="hasLinkedHint"
    :error-messages="errors"
    :is-disabled="disabled"
    :is-required="required"
    :is-label-hidden="isLabelHidden"
    :prepend-icon="prependIcon"
    :hide-error-append-icon="type === 'date' || type === 'datetime-local'"
    @open-hint="$emit('openHint')"
    @show-information="$emit('showInformation')"
  >
    <template #default="{ inputBindings }">
      <input
        :id="inputId"
        ref="inputField"
        v-bind="inputBindings"
        :value="modelValue"
        :placeholder="placeholder"
        :type="type"
        :name="name"
        :disabled="disabled"
        :required="required"
        :autocomplete="autocomplete"
        :maxlength="maxlength"
        :min="min"
        :max="max"
        :inputmode="inputmode"
        :style="styleBindings"
        @input="handleInput"
        @change="handleChange"
        @focus="emits('focus')"
      />
    </template>
    <template
      v-if="$slots.append"
      #append
    >
      <slot name="append"></slot>
    </template>
    <template
      v-if="maxlength && showMaxlengthCounter"
      #label-append
    >
      <div ref="textInputCounter">
        <TextInputCounter
          :current-value="currentInputCount"
          :max-value="maxlength"
        />
      </div>
    </template>
    <template
      v-if="$slots.errorMessage"
      #errorMessage="{ errorMessageId }"
    >
      <slot
        name="errorMessage"
        :error-message-id="errorMessageId"
      ></slot>
    </template>
  </InputGroup>
</template>
